<template>
  <f7-page name="messagePreview" class="modern-ui" @page:afteranimation="initPage">
    <bc-navbar :showLogo="true" :logoSrc="rafLogo"></bc-navbar>
    <div class="page-container">
      <referral-program-banner :referralProgramId="referralProgramType" />

      <template v-if="currentStep === AddReferralStep.NumberEntered">
        <section class="page-title">
          <h2 class="title">
            Here’s your message to {{name}}
          </h2>
          <p class="subtitle">Send a personal note that opens {{name}} intro page - not just a link.</p>
        </section>

        <div class="info-card" type="info">
          <div class="icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M21.5 16.0503V7.94625C21.4993 7.39798 21.3896 6.85532 21.1774 6.34976C20.9652 5.84421 20.6548 5.38584 20.264 5.00125C19.4679 4.21824 18.3956 3.77999 17.279 3.78125H6.72C5.60337 3.77999 4.53109 4.21824 3.735 5.00125C3.34442 5.38592 3.03412 5.84433 2.82211 6.34988C2.61011 6.85543 2.50062 7.39805 2.5 7.94625V16.0503C2.5 17.1553 2.945 18.2143 3.736 18.9953C4.53209 19.7783 5.60437 20.2165 6.721 20.2153H17.28C18.4 20.2153 19.473 19.7753 20.265 18.9953C20.6558 18.6107 20.9662 18.1523 21.1784 17.6467C21.3906 17.1412 21.4993 16.5985 21.5 16.0503Z"
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path
                d="M11.6308 15.9673C11.6308 15.1553 11.3067 14.3773 10.7287 13.8023C10.1498 13.2264 9.36635 12.9032 8.54975 12.9032C7.73315 12.9032 6.94975 13.2264 6.37075 13.8023C6.08497 14.0857 5.85814 14.423 5.70333 14.7946C5.54852 15.1662 5.46879 15.5647 5.46875 15.9673M14.5658 8.91825H18.1597M14.5658 11.9993H16.1057M14.5658 15.0803H18.1597M8.54975 12.1393C9.0945 12.1393 9.61695 11.9228 10.0021 11.5376C10.3873 11.1524 10.6037 10.63 10.6037 10.0853C10.6037 9.5405 10.3873 9.01805 10.0021 8.63285C9.61695 8.24765 9.0945 8.03125 8.54975 8.03125C8.005 8.03125 7.48255 8.24765 7.09735 8.63285C6.71215 9.01805 6.49575 9.5405 6.49575 10.0853C6.49575 10.63 6.71215 11.1524 7.09735 11.5376C7.48255 11.9228 8.005 12.1393 8.54975 12.1393Z"
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>

          <div class="title">Your friend's intro page</div>

          <p ref="">This text opens {{name}}'s personal intro page, where a friend will see your review, meet {{ salesPersonName }}, and
            have easy ways to start shopping.</p>
        </div>

        <div class="card elevated message-preview">
          <p class="title">Your message preview</p>
          <div class="card preview-text">
            <p ref="messageText">Hey {{name}}, I worked with {{ salesPersonName }} at {{ dealerName }} and had a great experience. I made this quick
              intro page for you in case you're thinking about a vehicle: <a class="preview-link" :href="referralLink" target="_blank" rel="external" @click.stop>{{ referralLink }}</a></p>
          </div>
        </div>

        <div class="info-card">
          <div class="icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M8 9H16M8 13H14M18 4C18.7956 4 19.5587 4.31607 20.1213 4.87868C20.6839 5.44129 21 6.20435 21 7V15C21 15.7956 20.6839 16.5587 20.1213 17.1213C19.5587 17.6839 18.7956 18 18 18H13L8 21V18H6C5.20435 18 4.44129 17.6839 3.87868 17.1213C3.31607 16.5587 3 15.7956 3 15V7C3 6.20435 3.31607 5.44129 3.87868 4.87868C4.44129 4.31607 5.20435 4 6 4H18Z"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>

          <p>We added {{ phone }} as {{name}}'s number. Tapping the button below will open your text app with this
            message ready to send.</p>
        </div>

        <button @click.stop="openMessage()" class="mui-button ripple accent">
          <send-icon/>
          Text {{name}}
        </button>
      </template>
      <template v-if="currentStep === AddReferralStep.NoNumberEntered">
        <section class="page-title">
          <h2 class="title">
            Here’s your message to {{name}}
          </h2>
          <p class="subtitle">We created a message and personalized link for you. We'll copy your message, then open
            your share menu so you can choose who to send it to.</p>
        </section>

        <div class="info-card tip-card" type="info">
          <div class="icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M11 10.9837C11 10.7185 11.1054 10.4641 11.2929 10.2766C11.4804 10.089 11.7348 9.98369 12 9.98369C12.2652 9.98369 12.5196 10.089 12.7071 10.2766C12.8946 10.4641 13 10.7185 13 10.9837V16.9837C13 17.2489 12.8946 17.5033 12.7071 17.6908C12.5196 17.8783 12.2652 17.9837 12 17.9837C11.7348 17.9837 11.4804 17.8783 11.2929 17.6908C11.1054 17.5033 11 17.2489 11 16.9837V10.9837ZM12 6.05469C11.7348 6.05469 11.4804 6.16004 11.2929 6.34758C11.1054 6.53512 11 6.78947 11 7.05469C11 7.3199 11.1054 7.57426 11.2929 7.76179C11.4804 7.94933 11.7348 8.05469 12 8.05469C12.2652 8.05469 12.5196 7.94933 12.7071 7.76179C12.8946 7.57426 13 7.3199 13 7.05469C13 6.78947 12.8946 6.53512 12.7071 6.34758C12.5196 6.16004 12.2652 6.05469 12 6.05469Z"
                fill="#033BAA" />
              <path fill-rule="evenodd" clip-rule="evenodd"
                d="M12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2ZM4 12C4 14.1217 4.84285 16.1566 6.34315 17.6569C7.84344 19.1571 9.87827 20 12 20C14.1217 20 16.1566 19.1571 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12C20 9.87827 19.1571 7.84344 17.6569 6.34315C16.1566 4.84285 14.1217 4 12 4C9.87827 4 7.84344 4.84285 6.34315 6.34315C4.84285 7.84344 4 9.87827 4 12Z"
                fill="currentColor" />
            </svg>
          </div>

          <div class="title">TIP from us</div>

          <p>Add {{name}}'s phone number on the previous screen if you want us to open a ready-to-send text automatically.
          </p>
        </div>

        <div class="card elevated message-preview">
          <p class="title">Your message preview</p>
          <div class="card preview-text">
            <p ref="copyText">Hey {{ name }}. If you're still shopping, I worked with {{ salesPersonName }} and it was a great experience. No pressure at all — here's the link if you want it: <a class="preview-link" :href="referralLink" target="_blank" rel="external" @click.stop>{{ referralLink }}</a></p>
          </div>
        </div>

        <button @click="copyAndShare()" class="mui-button ripple accent">
          <send-icon/>
          Copy message & open share menu
        </button>

        <p class="text-center">We'll copy the message first — then you can pick your friend and paste it.</p>
        <p class="text-center">
          Want the faster option? <br>
          Go back and add {{name}}'s phone number.</p>
      </template>

      <loader v-if="loading" :show="loading" />
    </div>
  </f7-page>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  LoopingRhombusesSpinner
} from 'epic-spinners'
import { AddReferralStep } from '../../shared/enums/referral.enum'
import { PAGES } from '../../shared/routes.constants'
import { notify } from '@/shared/notifications.js'
import { openSMSEditor } from '../../helpers/sms-editor'
import SendIcon from '@components/icons/send.icon.vue'
import ReferralProgramBanner from '@components/referral/referral-program-banner.vue'
export default {
  components: {
    'loader': LoopingRhombusesSpinner,
    SendIcon,
    'referral-program-banner': ReferralProgramBanner
  },
  computed: {
    ...mapState({
      salesPersonName: (state) => state.recommendFriend.mainInfo.salespersonFirstName,
      dealerName: (state) => state.recommendFriend.mainInfo.dealerName
    }),
    ...mapGetters({
      referrlaData: 'recommendFriend/getNewReferrlaData',
      sharedLinkData: 'recommendFriend/sharedLinkData',
      referralProgramType: 'recommendFriend/referralProgramType'
    }),
    name () {
      return this.referrlaData && this.referrlaData.firstName
    },
    phone () {
      return this.referrlaData && this.referrlaData.phone
    },
    code () {
      return this.referrlaData && this.referrlaData.code
    },
    referralLink () {
      return this.referrlaData && this.referrlaData.link
    }
  },
  methods: {
    ...mapActions({
      getSharedLink: 'recommendFriend/getShareLink',
      getReferralLinkWithCode: 'recommendFriend/getReferralLinkWithCode'
    }),
    initPage () {
      if (!this.referrlaData && !this.sharedLinkData) {
        this.redirect()
        return
      }
      if (!this.code) {
        console.error('No referral code')
        this.redirect()
        return
      }
      this.currentStep = this.referrlaData.phone
        ? AddReferralStep.NumberEntered
        : AddReferralStep.NoNumberEntered
    },
    copyAndShare () {
      this.loading = true
      const el = this.$refs.copyText
      const text = el.innerText
      navigator.clipboard.writeText(text).then(async () => {
        notify.success('Copied!')
        await this.getSharedLink()
        this.share(text)
      }, function (err) {
        notify.error('Could not copy text')
        console.error('Async: Could not copy text: ', err)
      })
    },
    share (text) {
      try {
        navigator
          .share({
            text
          })
          .then(() => this.redirectAfterAction())
      } catch (err) {
        notify.error('Error during share proccess')
      }
    },
    openMessage () {
      const el = this.$refs.messageText
      const text = el.innerText
      openSMSEditor(this.phone, text)
      notify.success('Message was sent successfully!')
      this.redirectAfterAction()
    },
    redirectAfterAction () {
      this.$f7.mainView.router.load({ url: `${PAGES.RecommendAFriendAdded}`, reloadAll: true })
    },
    redirect () {
      this.$f7.mainView.router.load({ url: `${PAGES.RecommendAFriend}`, reloadAll: true })
    }
  },
  data () {
    return {
      loading: false,
      rafLogo: require('@/assets/raf-logo-lg.png'),
      currentStep: null,
      AddReferralStep: AddReferralStep
    }
  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding-inline: 18px;
  padding-block: 8px 16px;
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;

  >*+* {
    margin-top: 24px;
  }

  .page-title {
    text-align: left;

    >.title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      padding: 0;
    }

    >.subtitle {
      font-size: 14px;
      color: #354C78;
      font-weight: 500;
    }
  }
}

.banner {
  width: 100%;
  max-width: 355px;
  margin-inline: auto;
}

.preview-link{
  word-break: break-all;
}

.info-card {
  display: grid;
  grid-template-columns: 42px 1fr;
  align-items: center;
  column-gap: 10px;
  row-gap: 4px;

  p {
    margin: 0;
    line-height: 1.2;
  }

  .icon {
    width: 42px;
  }

  &:has(.title){
    .icon{
      grid-row: span 2;
    }
  }
}

.message-preview {
  .title {
    font-weight: 600;
    font-size: 18px;
    margin: 0 0 8px 0;
  }

  .card {
    border-radius: 10px;
    background-color: #F9FAFB;

    p {
      line-height: 1.2;
    }
  }
}

.text-center {
  text-align: center;
}

</style>



// WEBPACK FOOTER //
// message-preview.vue?2b4521f2